/**
 * customerCell.tsx — helper render cho ô "Khách hàng" trong list view.
 * Avatar tròn (ảnh thật của khách, lùi về chữ cái đầu + màu pastel theo hash),
 * + chips Pancake/Đã cọc/KH cũ. Mirror layout FE bk OrdersPage.
 */

import { avatarSrcFor } from "@/lib/staff-avatar";
import type { OrderSummary } from "./api";

const PASTEL_BG = [
  "#FCA5A5", // red
  "#FCD34D", // amber
  "#86EFAC", // green
  "#93C5FD", // blue
  "#C4B5FD", // violet
  "#F9A8D4", // pink
  "#FDBA74", // orange
  "#A7F3D0", // emerald
  "#FDE68A", // yellow
];

function hashChar(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

const AVATAR_PX = 24;

/**
 * Vòng tròn avatar 24px: ẢNH THẬT của khách khi có, lùi về chữ cái đầu + màu
 * pastel theo hash khi không.
 *
 * `avatarUrl` là `customers.avatar_url` do avatarsync ghi từ bìa album final —
 * phần lớn khách chưa chạy đồng bộ, và khách vãng lai thì không có hồ sơ nên
 * không có ảnh; đường lùi về chữ cái đầu là đường ĐI THƯỜNG XUYÊN, không phải
 * đường dự phòng hiếm gặp. Giữ nguyên y hệt hình cũ để danh sách không đổi
 * dáng khi ảnh vắng mặt.
 *
 * Qua `avatarSrcFor` chứ không gắn thẳng vào `src`: hợp đồng API mô tả trường
 * này có thể là URL tuyệt đối HOẶC con trỏ kho ảnh (màn chat dùng chung dạng
 * đó). URL tuyệt đối đi qua hàm này vẫn nguyên vẹn, còn con trỏ thì được ghép
 * đúng biến thể — gắn thẳng con trỏ vào <img> là ảnh vỡ.
 *
 * `aria-hidden` cho cả hai nhánh: tên khách nằm ngay cạnh dưới dạng chữ, đọc
 * thêm lần nữa chỉ làm trình đọc màn hình lặp lại.
 */
export function customerAvatar(
  name: string | null | undefined,
  avatarUrl?: string | null | undefined,
): React.ReactElement {
  const trimmed = (name ?? "").trim();
  const src = avatarSrcFor(avatarUrl, AVATAR_PX);
  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className="h-6 w-6 shrink-0 rounded-full object-cover"
        aria-hidden
      />
    );
  }
  const ch = trimmed ? trimmed[0]!.toUpperCase() : "?";
  const bg = PASTEL_BG[hashChar(trimmed || "?") % PASTEL_BG.length];
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-foreground"
      style={{ background: bg }}
      aria-hidden
    >
      {ch}
    </span>
  );
}

const CHIP_CLS = "shrink-0 rounded px-1.5 py-px text-[10px] font-semibold border";

/** Render chip Pancake / Đã cọc / KH cũ dựa trên trường order. */
export function customerChips(o: OrderSummary): React.ReactElement | null {
  const isPancake = (o.leadSource ?? "").toLowerCase().includes("pancake");
  const isReturning = o.isReturning === true;
  const dep = Number(o.deposit) || 0;
  // Tiền khách đã trả vào — cộng lại khoản đã hoàn để hoàn tiền khiếu nại
  // không làm mất chip "Đã cọc" (lỗi #01 kiểm thử 16/09/2026).
  const paid = (Number(o.paid) || 0) + (Number(o.refunded) || 0);
  const dadCoc = dep > 0 && paid >= dep;

  const chips: React.ReactElement[] = [];
  if (isPancake) {
    chips.push(
      <span
        key="pancake"
        className={CHIP_CLS}
        style={{ background: "#FFF7E0", color: "#966718", borderColor: "#F0D391" }}
      >
        🥞 Pancake
      </span>,
    );
  }
  if (dadCoc) {
    chips.push(
      <span
        key="coc"
        className={CHIP_CLS}
        style={{ background: "#E6F8E6", color: "#347C34", borderColor: "#A6D8A6" }}
      >
        💰 Đã cọc
      </span>,
    );
  }
  if (isReturning) {
    chips.push(
      <span
        key="kh-cu"
        className={CHIP_CLS}
        style={{ background: "#FFF1D6", color: "#A8740B", borderColor: "#F0C271" }}
      >
        📒 KH cũ
      </span>,
    );
  }
  if (chips.length === 0) return null;
  return <span className="flex flex-wrap items-center gap-1">{chips}</span>;
}
